/// <amd-module name="TFS/FeatureManagement/FeaturePanelService" />
import * as React from "react";

import { format } from "VSS/Core/Util/String";
import { IVssService, Services, VssService } from "VSS/Platform/Context";
import { IVssLayoutManager, ReactRootComponent } from "VSS/Platform/Layout";
import { IVssPageService } from "VSS/Platform/Page";

import { ITfsPageService } from "Tfs/Platform/Page";

import FeatureResources = require("TFS/FeatureManagement/Resources");
import { ITfsFeatureService } from "TFS/FeatureManagement/Types";
import { ContributedFeatureSettingScope, ContributedFeatureState } from "./Contracts/FeatureManagement";
import { FeatureManagementPanelComponent, IFeatureManagementScope } from "./FeaturePanel";

// There can only be a single feature panel at any given time.
let panelOpen: boolean;

/**
 * Service for displaying the feature management panel
 */
export interface IFeaturePanel extends IVssService {
    /**
     * Show the feature panel
     *
     * @param initialScope Optional scope to select when the panel is first opened.
     */
    show: (initialScope?: ContributedFeatureSettingScope) => void;
}

class TfsFeaturePanel extends VssService implements IFeaturePanel {
    private changesMade: boolean;
    private isHosted: boolean;

    public show(initialScope?: ContributedFeatureSettingScope): void {
        // If the panel were previously opened we will ignore the request to show it again.
        if (panelOpen) {
            return;
        }

        const pageService = this.pageContext.getService<IVssPageService>("IVssPageService");
        const pageData = pageService.getData();

        if (pageData) {
            this.isHosted = pageData.isHosted;
        }

        const tfsPageService = this.pageContext.getService<ITfsPageService>("ITfsPageService");
        const tfsPageData = tfsPageService.getData();
        const scopeValues: { [scope: string]: string } = {};

        if (tfsPageData && tfsPageData.project) {
            scopeValues["project"] = tfsPageData.project.id;
        }

        const teamData = tfsPageService.getHorizontalNavigationTeamData();
        if (teamData) {
            scopeValues["team"] = teamData.id;
        }

        // Initialize the FeatureManagementPanelComponent with feature data from the feature service.
        // It will show loading until the data has been resolved.
        const supportedScopes: IFeatureManagementScope[] = [];

        // "My" features
        if (pageData && pageData.user) {
            supportedScopes.push({
                featureScope: { userScoped: true, settingScope: "" },
                displayName: format(FeatureResources.ManageFeaturesScopeMe, pageData.user.displayName),
                canManage: true
            });
        }

        // "Team" features
        if (teamData) {
            supportedScopes.push({
                featureScope: { userScoped: false, settingScope: "team" },
                displayName: format(FeatureResources.ManageFeaturesScopeTeam, teamData.name),
                scopeWarningText: FeatureResources.ToggleFeatureForTeamWarning
            });
        }

        // "Project" features
        if (tfsPageData && tfsPageData.project) {
            supportedScopes.push({
                featureScope: { userScoped: false, settingScope: "project" },
                displayName: format(FeatureResources.ManageFeaturesScopeProject, tfsPageData.project.name),
                scopeWarningText: FeatureResources.ToggleFeatureForProjectWarning
            });
        }

        // "Account/Collection (Host)" features
        if (pageData) {
            supportedScopes.push({
                featureScope: { userScoped: false, settingScope: "" },
                displayName: this.isHosted
                    ? format(FeatureResources.ManageFeaturesScopeAccount, pageData.hostName)
                    : format(FeatureResources.ManageFeaturesScopeCollection, pageData.hostName),
                scopeWarningText: this.isHosted ? FeatureResources.ToggleFeatureForAccountWarning : FeatureResources.ToggleFeatureForCollectionWarning
            });
        }

        // Setup the featureService to listen for changes to feature states.
        const featureService = this.pageContext.getService<ITfsFeatureService>("ITfsFeatureService");
        featureService.subscribe(this._onFeatureStateUpdate);

        const layoutService = this.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
        layoutService.renderCallout((onDismiss: () => void) => {
            // Have a local function for closing the panel cleaning up our state.
            const onPanelDismiss = (): void => {
                panelOpen = false;

                featureService.unsubscribe(this._onFeatureStateUpdate);

                // Refresh the page if changes were made while the panel was opened.
                if (this.changesMade) {
                    window.location.reload();
                } else {
                    onDismiss();
                }
            };

            // Render the feature panel with the initial props.
            return React.createElement(
                FeatureManagementPanelComponent,
                {
                    featureService: featureService,
                    isHosted: this.isHosted,
                    onDismiss: onPanelDismiss,
                    supportedScopes: supportedScopes,
                    scopeValues: scopeValues,
                    initialScope: initialScope
                },
                null
            );
        });

        panelOpen = true;
    }

    private _onFeatureStateUpdate = (featureState: ContributedFeatureState): void => {
        this.changesMade = true;
    };
}

Services.add("IFeaturePanel", { serviceFactory: TfsFeaturePanel });
